/**
 * Solde du compte PayDunya du restaurant.
 *
 * Le restaurateur ne depose pas ses cles -- la plateforme le fait pour lui, a
 * son inscription. Il lui reste cette vue, qui est la contrepartie de cette
 * confiance : il doit pouvoir verifier que son argent arrive.
 *
 * Le montant n'est pas calcule par Scan2Eat. Il vient du prestataire, qui previent
 * lui-meme qu'il s'agit d'une approximation ; l'ecran le repete plutot que de
 * promettre une exactitude que la source ne donne pas.
 *
 * Voir docs/adr/0019-cles-marchandes-tenues-par-la-plateforme.md.
 */

import { useEffect, useState } from 'react';
import type { PaymentBalanceDTO } from '@scan2eat/shared';
import { formatMoney } from '@scan2eat/shared';

import { apiFetch } from '../api/client.js';
import { labels } from '../labels.js';

export function BalancePage() {
  const [balance, setBalance] = useState<PaymentBalanceDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = () => {
    setLoading(true);
    setFailed(false);
    return apiFetch<PaymentBalanceDTO>('/admin/payment-account/balance')
      .then(setBalance)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    // La barre de navigation vient d'AdminLayout, comme pour toute page
    // authentifiee : la page ne rend que son contenu.
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{labels.balance.title}</h1>
          <p className="page-subtitle">{labels.balance.intro}</p>
        </div>
      </header>

      <div style={{ maxWidth: 620 }}>

        {loading ? (
          <div className="skeleton" style={{ height: 150, borderRadius: 'var(--radius-lg)' }} />
        ) : failed ? (
          <section className="page-card" style={{ padding: 'var(--space-5)', textAlign: 'center' }}>
            {/* Le prestataire injoignable n'est pas un solde nul : ne rien
                afficher vaut mieux qu'afficher zero. */}
            <p role="alert" style={{ marginTop: 0 }}>{labels.balance.unavailable}</p>
            <button onClick={() => void load()} className="btn-primary">
              {labels.errors.retry}
            </button>
          </section>
        ) : balance?.configured && balance.balanceMinor === null ? (
          <section className="page-card" style={{ padding: 'var(--space-5)' }}>
            {/* Compte valide, solde non communique : ce n'est pas une panne de
                l'encaissement, et l'ecran doit le dire. */}
            <p style={{ margin: 0, fontWeight: 600 }}>{labels.balance.unknown}</p>
            {/* Deux causes distinctes, deux explications : le bac a sable ne
                sert aucun solde, alors qu'en production c'est l'API de
                deboursement qui peut ne pas etre activee. Donner la mauvaise
                enverrait le restaurateur chercher un reglage inexistant. */}
            <p style={{ ...noticeStyle, marginBottom: 0 }}>
              {balance.mode === 'TEST' ? labels.balance.unknownTest : labels.balance.unknownDetail}
            </p>
          </section>
        ) : balance?.configured ? (
          <section className="page-card" style={{ padding: 'var(--space-5)' }}>
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {labels.balance.available}
            </p>
            <p style={{ margin: 'var(--space-2) 0 0', fontSize: '2rem', fontWeight: 700 }}>
              {formatMoney(balance.balanceMinor ?? 0, balance.currency ?? 'XOF')}
            </p>

            {balance.mode === 'TEST' && (
              <p style={noticeStyle}>{labels.balance.testMode}</p>
            )}

            <p style={noticeStyle}>{labels.balance.approximate}</p>

            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginTop: 'var(--space-4)' }}>
              <button onClick={() => void load()} className="btn-ghost">
                {labels.balance.refresh}
              </button>
              {balance.readAt && (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  {labels.balance.readAt} {new Date(balance.readAt).toLocaleTimeString('fr-FR')}
                </span>
              )}
            </div>
          </section>
        ) : (
          <section className="page-card" style={{ padding: 'var(--space-5)' }}>
            {/* Etat normal entre l'inscription et la reception des cles. */}
            <p style={{ margin: 0, fontWeight: 600 }}>{labels.balance.notConfigured}</p>
            <p style={{ ...noticeStyle, marginBottom: 0 }}>{labels.balance.notConfiguredDetail}</p>
          </section>
        )}
      </div>
    </div>
  );
}

const noticeStyle: React.CSSProperties = {
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-3)',
  marginBottom: 0,
};
